import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, ArrowRight } from "lucide-react";

export default function AgentDashboard() {
  const { agent } = useAuth();
  const [leads, setLeads] = useState([]);
  const [pay, setPay] = useState({ approved_leads: 0, pending_leads: 0, total_paid: 0, total_pending: 0 });

  useEffect(() => {
    api.get("/agents/leads").then(r => setLeads(r.data || [])).catch(() => {});
    api.get("/agents/payouts").then(r => setPay(r.data || {})).catch(() => {});
  }, []);

  const stats = [
    { label: "Leads created", value: leads.length },
    { label: "Approved", value: pay.approved_leads || 0 },
    { label: "In pipeline", value: pay.pending_leads || 0 },
    { label: "Pending payout (₹)", value: Math.round(pay.total_pending || 0) },
  ];

  return (
    <div>
      <div className="overline">Hi {agent?.name?.split(" ")[0]}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">Your agent dashboard</h1>
      <p className="text-sm text-ink-muted mt-1">Code <span className="font-mono font-bold text-saffron">{agent?.code}</span> · {agent?.agent_type}</p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-ink-muted">{s.label}</div>
            <div className="font-display font-extrabold text-2xl sm:text-3xl mt-1">{Number(s.value).toLocaleString("en-IN")}</div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-5">
        <Link to="/agent/new-lead" className="card-flat p-6 group lg:col-span-1">
          <div className="h-11 w-11 rounded-xl grid place-items-center bg-saffron/10 text-saffron"><Plus className="h-5 w-5"/></div>
          <h3 className="font-display text-xl font-bold mt-4">Submit a new lead</h3>
          <p className="text-sm text-ink-muted mt-1">Capture customer details and submit instantly.</p>
          <div className="mt-4 text-saffron font-semibold inline-flex items-center gap-1.5">Create <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform"/></div>
        </Link>

        <div className="card-flat p-6 lg:col-span-2">
          <h3 className="font-display text-xl font-bold">Recent leads</h3>
          <div className="mt-4 space-y-3">
            {leads.slice(0, 5).map(l => (
              <div key={l.id} className="flex items-center justify-between border-b border-slate-100 last:border-0 pb-3 last:pb-0">
                <div>
                  <div className="text-sm font-semibold">{l.full_name} <span className="text-ink-muted font-normal">· {l.loan_type}</span></div>
                  <div className="text-xs text-ink-muted">{new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium" })}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${l.status === "Approved" ? "bg-green-100 text-green-700" : l.status === "Rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{l.status}</span>
              </div>
            ))}
            {leads.length === 0 && <div className="text-sm text-ink-muted">No leads yet. Hit "Submit a new lead" to get started.</div>}
          </div>
          {leads.length > 0 && <Link to="/agent/leads" className="text-sm font-semibold text-saffron mt-4 inline-flex items-center gap-1">View all <ArrowRight className="h-4 w-4"/></Link>}
        </div>
      </div>
    </div>
  );
}
